import { Injectable } from "@angular/core";
import { AlertController, AlertOptions, ModalController } from "@ionic/angular";

import type { AlertData, AlertId, WithDefinedId } from "./alerts.data";
import { alertTemplateForId, hasDefinedId } from "./alerts.data";
import { AlertModalComponent } from "../../pages/alert-modal/alert-modal.component";

import { console } from "#/log";

/**
 * A service for showing different types of alert dialogs.  Supported alert
 * types include:
 *  - Default Alert: alert with a title, message and OK button
 *  - Confirm Alert: alert with a title, message, OK button, Cancel button
 *  - Modal Alert: alert with a title, custom HTML content, and OK button
 *  - Input Alert: alert with title, input fields, OK / Cancel buttons
 * Dynamic values can be injected into message template strings.
 * See `alerts.data` for more info.
 */

@Injectable()
export class AlertService {
  private activeAlerts: Array<AlertId> = [];

  constructor(
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
  ) {}

  /**
   * Shows an alert based on a corresponding ID from the alert
   * data file.  Any values in the args array are placed into the
   * title and message.
   */
  showAlert(id: AlertId, args: Array<string> = []): Promise<boolean> {
    let alertData = this.getAlertObject(id, args);

    return new Promise<boolean>((resolve, reject) => {
      alertData.buttons = [{ text: "OK", handler: () => resolve(true) }];

      // Call createAlert and handle the possibility of it being undefined
      const alertPromise = this.createAlert(alertData);

      if (alertPromise) {
        alertPromise
          .then((alert) => {
            if (alert) {
              // Correctly attach the onDidDismiss handler to the alert instance
              alert.onDidDismiss().then(() => {
                resolve(true);
              });
            } else {
              resolve(false); // Handle the case where alert is undefined
            }
          })
          .catch((error) => {
            console.error("Error creating alert:", error);
            reject(false);
          });
      } else {
        // Handle the case where createAlert returned undefined
        console.error("Alert creation function returned undefined");
        reject(false);
      }
    }).then((data) => {
      this.deactivateAlert(alertData.id!);
      return data;
    });
  }

  /**
   * Shows an alert using a modal, which allows for an image.
   */
  showModalAlert(id: AlertId, args: Array<string> = []): Promise<boolean> {
    let alertData = this.getAlertObject(id, args);
    console.debug("alerts.service: modal alert data", alertData);

    // Create the modal with an options object
    return this.modalCtrl
      .create({
        component: AlertModalComponent, // Specify the component
        componentProps: alertData, // Pass the data as componentProps
      })
      .then((alert) => {
        alert.present();

        // Handle dismissal of the modal
        return alert.onDidDismiss().then((detail) => {
          this.deactivateAlert(alertData.id!);

          // Check if the detail contains data and determine the return value
          if (detail && detail.data) {
            // Assuming the data returned has a boolean property
            return !!detail.data; // Adjust this based on how your modal returns data
          }
          return false; // Default return value if no data was provided
        });
      });
  }

  /**
   * Shows a confirm alert with the corresponding alert id.
   * Returns a promise with a true value if confirmed, or false
   * if not confirmed.
   */
  showConfirmAlert(id: AlertId, args: Array<string> = []): Promise<boolean> {
    let alertData = this.getAlertObject(id, args);
    return new Promise<boolean>((resolve, reject) => {
      alertData.buttons = [
        { text: "Yes", handler: () => resolve(true) },
        { text: "No", handler: () => resolve(false) },
      ];
      this.createAlert(alertData);
    }).then((data) => {
      this.deactivateAlert(alertData.id!);
      return data;
    });
  }

  /**
   * Shows an alert with inputs and shows the appropriate buttons.
   */
  showInputAlert(id: AlertId, args: Array<string> = []): Promise<boolean> {
    let alertData = this.getAlertObject(id, args);

    return new Promise<boolean>((resolve) => {
      alertData.buttons = [
        { text: "Cancel", role: "cancel", handler: () => resolve(false) },
        { text: "OK", handler: (data: any) => resolve(!!data) }, // Ensure a boolean is returned
      ];

      const alertPromise = this.createAlert(alertData);

      // Check if alertPromise is defined
      if (alertPromise) {
        alertPromise
          .then((alert) => {
            if (alert) {
              alert.onDidDismiss().then(() => {
                resolve(false);
              });
            } else {
              resolve(false); // In case the alert could not be created
            }
          })
          .catch(() => {
            resolve(false); // Handle error in alert creation
          });
      } else {
        resolve(false); // Handle the case where createAlert returns undefined
      }
    }).then((data) => {
      this.deactivateAlert(alertData.id!);
      return data;
    });
  }

  /**
   * Creates the alert using Ionic's alert controller
   */
  private createAlert(
    alertObject: AlertOptions,
  ): Promise<HTMLIonAlertElement> | undefined {
    if (hasDefinedId(alertObject)) {
      if (!this.isAlertActive(alertObject.id)) {
        this.activeAlerts.push(alertObject.id);

        return this.alertCtrl.create(alertObject).then((alert) => {
          alert.present().then(() => {
            // HACK: Manually inject HTML into the message container
            if (alert.message) {
              const msgEl = document.querySelector("ion-alert .alert-message");
              if (msgEl) {
                msgEl.innerHTML = alert.message as string;
              }
            }
          });
          return alert;
        });
      } else {
        console.debug("alert-service: alert is already active", alertObject);
        return undefined;
      }
    }
    return undefined;
  }

  /**
   * Checks to see if the alert is already active
   */
  private isAlertActive(alertId: AlertId): boolean {
    return this.activeAlerts.indexOf(alertId) > -1;
  }

  /**
   * Alert de-activated
   */
  private deactivateAlert(alertId: AlertId) {
    if (this.isAlertActive(alertId)) {
      this.activeAlerts.splice(this.activeAlerts.indexOf(alertId), 1);
    }
  }

  /**
   * Creates the alert options object based on the id for the alert
   * data and a list of values to interpolate.  You can interpolate the
   * message string using ${} with the appropriate index inside of args
   * of the value you would like to use.
   */
  private getAlertObject(
    id: AlertId,
    args: Array<string>,
  ): WithDefinedId<AlertData> {
    const alertData = alertTemplateForId(id);
    let message = "";
    alertData.header = this.getInterpolatedString(alertData.header!, args);
    if (alertData.hasOwnProperty("message")) {
      if (typeof alertData.message === "string") {
        message += this.getInterpolatedString(alertData.message, args);
      } else {
        message += alertData.message; // It’s IonicSafeString – just use it directly
      }
    }
    if (alertData.hasOwnProperty("map")) {
      message += this.getMappedString(alertData.map!, args);
    }
    alertData.message = message;
    if (alertData.hasOwnProperty("image")) {
      alertData.image = this.getInterpolatedString(alertData.image!, args);
    }
    return alertData;
  }

  /**
   * Builds a string based on the message map in the alert data and
   * the keys passed.
   */
  private getMappedString(
    mapData: Record<string, any>,
    keys: Array<string>,
  ): string {
    let message = keys
      .map((key) => {
        if (key in mapData) {
          // Using 'in' to check for key existence
          return mapData[key];
        } else {
          throw new Error("no corresponding map data for key: " + key);
        }
      })
      .filter((s) => !!s)
      .join("<br /><br />");

    return message;
  }

  /**
   * populates the string with values from the array
   * e.g. ${0} is replaced with the value at index 0 in the values array
   */
  private getInterpolatedString(text: string, values: Array<string>) {
    values.forEach((v: string, i: number) => {
      text = text.replace("${" + i + "}", v);
    });
    return text;
  }
}
